import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "./api";
import { IconClose } from "./Icons";

// A touch over the server's 30-min run cap (RUN_TIMEOUT_SECONDS): a run still
// "started" past this means the server died mid-run.
const RUN_TIMEOUT_MS = 31 * 60 * 1000;

function displayStatus(run) {
  if (run.status === "started" && run.started_at &&
      Date.now() - new Date(run.started_at).getTime() >= RUN_TIMEOUT_MS) {
    return { label: "Interrupted", cls: "err" };
  }
  switch (run.status) {
    case "ok": return { label: "Succeeded", cls: "ok" };
    case "error": return { label: "Failed", cls: "err" };
    case "skipped": return { label: "Skipped (server offline)", cls: "warn" };
    case "started": return { label: "Running…", cls: "run" };
    default: return { label: run.status || "Unknown", cls: "" };
  }
}

function fmtWhen(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString([], {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function fmtDuration(run) {
  if (!run.started_at || !run.finished_at) return null;
  const ms = new Date(run.finished_at).getTime() - new Date(run.started_at).getTime();
  if (ms < 0) return null;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

/** Run-log history for one scheduled task, opened from the panel's info icon. */
export default function ScheduledTaskLogsModal({ envId, task, onClose, onOpenThread }) {
  const [runs, setRuns] = useState(null); // null = loading
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.listScheduledTaskRuns(envId, task.id)
      .then((rows) => { if (alive) setRuns(rows || []); })
      .catch((e) => { if (alive) setError(e.message || "Failed to load logs"); });
    return () => { alive = false; };
  }, [envId, task.id]);

  // ponytail: portal to body, same reason as ScheduledTaskModal.
  return createPortal(
    <div className="ca-mcp-modal-backdrop" onClick={onClose}>
      <div className="ca-mcp-modal ca-sched-logs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ca-sched-logs-head">
          <h3>Run log — {task.name}</h3>
          <button type="button" className="ca-btn ca-btn--xs ca-btn--ghost"
                  onClick={onClose} aria-label="Close"><IconClose size={14} /></button>
        </div>
        {error && <div className="ca-mcp-modal-err">{error}</div>}
        {!error && runs === null && <div className="ca-sched-empty">Loading…</div>}
        {!error && runs && runs.length === 0 && (
          <div className="ca-sched-empty">No runs yet.</div>
        )}
        {runs && runs.length > 0 && (
          <ul className="ca-sched-logs-list">
            {runs.map((run) => {
              const status = displayStatus(run);
              const dur = fmtDuration(run);
              return (
                <li key={run.id} className="ca-sched-log-row">
                  <div className="ca-sched-log-top">
                    <span className={`ca-sched-log-status ${status.cls}`}>{status.label}</span>
                    <span className={`ca-sched-log-trigger ${run.trigger}`}>{run.trigger}</span>
                    <span className="ca-sched-log-when">{fmtWhen(run.started_at)}</span>
                    {dur && <span className="ca-sched-log-dur">· {dur}</span>}
                  </div>
                  {run.error && <div className="ca-sched-log-error">{run.error}</div>}
                  {run.thread_id && (
                    <button type="button" className="ca-sched-log-open"
                            onClick={() => { onOpenThread(run.thread_id); onClose(); }}>
                      Open chat
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>,
    document.body
  );
}
